import { useEffect, useRef } from 'react';

/** Cloudflare Turnstile: the "Verifying…" human check under sign-in and sign-up. */
export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAFqtJIsuP1dNJP87';
type TS = { render: (el: HTMLElement, o: Record<string, unknown>) => string; reset: (id?: string) => void; remove: (id: string) => void };
declare global { interface Window { turnstile?: TS } }

let loading: Promise<void> | null = null;
function load() {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise((ok, bad) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true; s.onload = () => ok(); s.onerror = () => { loading = null; bad(new Error('turnstile')); };
    document.head.appendChild(s);
  });
  return loading;
}

export function Turnstile({ onToken, theme = 'dark', resetKey }: { onToken: (t: string | null) => void; theme?: 'dark' | 'light'; resetKey?: unknown }) {
  const box = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const cb = useRef(onToken); cb.current = onToken;
  useEffect(() => {
    let gone = false;
    load().then(() => {
      if (gone || !box.current || !window.turnstile) return;
      id.current = window.turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY, theme, size: 'flexible',
        callback: (t: string) => cb.current(t), 'expired-callback': () => cb.current(null), 'error-callback': () => cb.current(null),
      });
    }).catch(() => cb.current(null));
    return () => { gone = true; if (id.current && window.turnstile) window.turnstile.remove(id.current); id.current = null; };
  }, [theme]);
  // A new token is needed after each attempt.
  useEffect(() => { if (resetKey !== undefined && id.current && window.turnstile) { window.turnstile.reset(id.current); cb.current(null); } }, [resetKey]);
  return <div className="ts-box" ref={box} />;
}
